import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGLineChart, PGBarChart, PGPieChart } from '../../components/primitives/PGChart';
import { getBranch, peersOf, INSURERS } from '../../utils/orgData';
import { PG } from '../../utils/theme';

const { FiDollarSign, FiPercent, FiTrendingUp, FiInbox, FiClock } = FiIcons;

const fmtR = (v) => 'R ' + Math.round(v).toLocaleString('en-ZA');

function makeDailyRevenue(seed, target) {
  const out = [];
  let cum = 0;
  const dayTarget = target / 30;
  for (let i = 1; i <= 30; i++) {
    const daily = dayTarget * (0.6 + Math.abs(Math.sin(i * 0.8 + seed)) * 0.9);
    cum += daily;
    out.push(+cum.toFixed(0));
  }
  return out;
}

function makeDailyTarget(target) {
  const dayTarget = target / 30;
  return Array.from({ length: 30 }, (_, i) => Math.round(dayTarget * (i + 1)));
}

export default function FinanceBranchView() {
  const { branchId } = useParams();
  const branch = getBranch(branchId);

  const daily = useMemo(() => branch ? makeDailyRevenue(branch.id.length, branch.finance.revenueMTD) : [], [branch]);
  const target = useMemo(() => branch ? makeDailyTarget(branch.finance.revenueMTD * 1.05) : [], [branch]);

  if (!branch) {
    return <div className="h-full flex items-center justify-center text-neutral-500 font-mono-tech text-xs uppercase tracking-widest">Branch not found</div>;
  }

  const peers = peersOf(branch);
  const peerAvgGp = peers.length ? +(peers.reduce((s, p) => s + p.finance.gpPct, 0) / peers.length).toFixed(1) : branch.finance.gpPct;

  const cost = [
    { name: 'Glass / OEM parts', value: 42 },
    { name: 'Labour', value: 28 },
    { name: 'Consumables', value: 14 },
    { name: 'Overhead', value: 16 },
  ];

  // Insurer mix scaled down for this branch
  const scale = branch.ops.jobsMTD / 14000;
  const insurerMix = INSURERS.map((ins) => ({
    name: ins.name,
    jobs: Math.max(1, Math.round(ins.jobs * scale)),
    revShare: ins.revenueShare,
  }));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="flex items-baseline justify-between flex-shrink-0">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500">Finance / Branch drilldown</div>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="font-doto text-3xl text-white">{branch.name}</h1>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{branch.town} &middot; {branch.country}</span>
          </div>
        </div>
        <StatusPill tone={branch.finance.gpPct >= peerAvgGp ? 'ok' : 'warn'}>Peer: {branch.peerGroup}</StatusPill>
      </div>

      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard title="REVENUE MTD" value={`R ${(branch.finance.revenueMTD / 1e6).toFixed(2)}M`} icon={FiDollarSign} accent={PG.red} />
        <KPICard title="GP%" value={branch.finance.gpPct} unit="%" icon={FiPercent} accent={branch.finance.gpPct >= peerAvgGp ? PG.emerald : PG.amber} target={`${peerAvgGp}% peer`} />
        <KPICard title="EBITDA%" value={branch.finance.ebitdaPct} unit="%" icon={FiTrendingUp} accent={PG.emerald} />
        <KPICard title="DEBTORS DAYS" value={branch.finance.debtorsDays} unit="days" icon={FiClock} accent={PG.amber} target="<35" />
        <KPICard title="CASH TODAY" value={fmtR(branch.finance.cashCollected)} icon={FiInbox} accent={PG.cyan} />
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <Panel title="REVENUE_MTD_VS_TARGET" subtitle="cumulative — daily" className="col-span-7">
          <div className="h-full p-2">
            <PGLineChart
              categories={Array.from({ length: 30 }, (_, i) => `D${i + 1}`)}
              series={[
                { name: 'Actual', data: daily, itemStyle: { color: PG.red }, lineStyle: { color: PG.red, width: 2 } },
                { name: 'Target', data: target, itemStyle: { color: PG.neutral500 }, lineStyle: { color: PG.neutral500, width: 1, type: 'dashed' }, areaStyle: undefined },
              ]}
              yFormatter={(v) => (v / 1e6).toFixed(1) + 'M'}
            />
          </div>
        </Panel>

        <Panel title="COST_STRUCTURE" subtitle="%" className="col-span-5">
          <div className="h-full p-2">
            <PGPieChart data={cost} />
          </div>
        </Panel>

        <Panel title="PEER_GP%_COMPARISON" subtitle={`vs ${peers.length} peers`} className="col-span-5">
          <div className="h-full p-2">
            <PGBarChart
              categories={[branch.name, ...peers.slice(0, 5).map((p) => p.name)]}
              series={[
                {
                  name: 'GP%',
                  data: [
                    { value: branch.finance.gpPct, itemStyle: { color: PG.red } },
                    ...peers.slice(0, 5).map((p) => ({ value: p.finance.gpPct, itemStyle: { color: PG.neutral500 } })),
                  ],
                },
              ]}
              yFormatter={(v) => v + '%'}
            />
          </div>
        </Panel>

        <Panel title="JOBS_BY_INSURER_MIX" subtitle="branch scale" className="col-span-7">
          <div className="h-full p-2">
            <PGBarChart
              categories={insurerMix.map((i) => i.name)}
              series={[
                {
                  name: 'Jobs',
                  data: insurerMix.map((i, idx) => ({
                    value: i.jobs,
                    itemStyle: { color: [PG.red, PG.amber, PG.emerald, PG.cyan, PG.violet, PG.white, PG.neutral500][idx % 7] },
                  })),
                },
              ]}
            />
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
